import { useQuery } from '@tanstack/react-query';
import { PodgladKaskady } from '@/components/PodgladKaskady';
import { ZmianaKlucza } from '@/components/ZmianaKlucza';
import { checkGroupCode, fetchRenameImpact, zmienKodGrupyTowarowej } from '@/lib/item-groups-api';
import { useI18n } from '@/lib/i18n';

/**
 * ZMIANA KODU GRUPY TOWAROWEJ — najszersza z czterech odmian okna zmiany klucza.
 *
 * ⚠️ PODGLĄD JEST TU OBOWIĄZKOWY Z DWÓCH POWODÓW NARAZ. `Rationing.ITEMKEY` idzie bez filtru po
 * `ITEMTYPE` (decyzja D1), więc zmiana rusza też regułę limitu TOWARU o tym samym kodzie; a wpisy
 * dostępu do podgrup przepisujemy PONAD to, co robi SPS (decyzja D2), bo inaczej wskazywałyby kod,
 * którego już nie ma. Operator ma zobaczyć jedno i drugie, zanim kliknie.
 *
 * ⚠️ `Items.ITEMGROUP` ma w SPS 16 znaków — mniej niż numer towaru i numer karty. SPS obciąłby
 * dłuższy BEZ ostrzeżenia, a wtedy klucz przestałby zgadzać się z read-modelem.
 */
export function ZmianaKoduGrupyTowarowej({
  grupaId,
  islandId,
  obecnyKod,
  nazwa,
  onClose,
  onSaved,
}: {
  grupaId: string;
  islandId: string;
  obecnyKod: string;
  nazwa: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const zakres = useQuery({
    queryKey: ['item-group', 'rename-impact', grupaId, islandId],
    queryFn: () => fetchRenameImpact(grupaId, islandId),
  });

  return (
    <ZmianaKlucza
      tytul={t('ig.codeChangeTitle')}
      podpis={`${nazwa} · ${obecnyKod}`}
      wstep={t('ig.codeChangeIntro')}
      ostrzezenie={t('ig.codeChangeWarn')}
      etykietaPola={t('ig.codeChangeNew')}
      obecny={obecnyKod}
      limit={16}
      podglad={zakres.data ? <PodgladKaskady targets={zakres.data.targets} /> : null}
      sprawdz={(v) => checkGroupCode(islandId, v)}
      zapisz={async (nowy) => {
        const r = await zmienKodGrupyTowarowej(grupaId, { groupId: islandId, nowyKod: nowy });
        return t('ig.codeChanged').replace('{stary}', r.stary).replace('{nowy}', r.nowy);
      }}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}
